// frontend/src/services/roles.ts
import http from "./http";

export type RoleScope = "GLOBAL" | "COMPANY";

export interface Role {
  id: number;
  code: string;
  name: string;
  scope?: RoleScope;
  company_id?: number | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface Page<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    pages: number;
    total: number;
  };
}

type RolesResponse = Role[] | Paginated<Role> | Page<Role> | { data: Role[] };

const BASE = "/devioz/roles"; // ✅ SIN "/api"

function extractArray<T = any>(resp: any): T[] {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp as T[];
  if (Array.isArray(resp.data)) return resp.data as T[];
  if (Array.isArray(resp?.data?.data)) return resp.data.data as T[];
  return [];
}

function isPage<T>(r: any): r is Page<T> {
  return !!(r && Array.isArray(r.data) && r.pagination && typeof r.pagination?.page === "number");
}

function isPaginated<T>(r: any): r is Paginated<T> {
  return !!(r && Array.isArray(r.data) && typeof r.page === "number" && typeof r.pageSize === "number");
}

export async function listRoles(params?: {
  q?: string;
  page?: number;
  pageSize?: number;
  scope?: RoleScope;
  company_id?: number | null;
}): Promise<Role[] | Paginated<Role> | Page<Role>> {
  const res = (await http.get(BASE, { params })) as RolesResponse;

  // ✅ si viene paginado, respeta estructura
  if (isPage<Role>(res) || isPaginated<Role>(res)) return res;

  // ✅ si viene array / {data: []} lo normalizamos a array
  return extractArray<Role>(res);
}

export async function getRole(id: number | string): Promise<Role> {
  return (await http.get(`${BASE}/${id}`)) as Role;
}

export async function createRole(payload: {
  code: string;
  name: string;
  scope?: RoleScope;
  company_id?: number | null;
}): Promise<Role> {
  return (await http.post(BASE, payload)) as Role;
}

export async function updateRole(
  id: number | string,
  payload: Partial<{
    code: string;
    name: string;
    scope: RoleScope;
    company_id: number | null;
  }>
): Promise<Role> {
  return (await http.put(`${BASE}/${id}`, payload)) as Role;
}

export async function deleteRole(
  id: number | string
): Promise<{ id: number | string; deleted?: boolean }> {
  return (await http.delete(`${BASE}/${id}`)) as { id: number | string; deleted?: boolean };
}

export default { listRoles, getRole, createRole, updateRole, deleteRole };
